// Typed API client for TanStack Query queryFns/mutationFns.
//
// - Cookie-first auth: `credentials: 'include'` sends the httpOnly session
//   cookie (the app moved off XSS-readable localStorage tokens). No Authorization
//   header is attached here.
// - Throws on any non-2xx (or a JSON body with `success:false`) so react-query
//   treats it as an error instead of caching a failure as data.
// - Some endpoints wrap their payload (`{events:[...]}`, `{portal_requests:[...]}`,
//   `{comms:[...]}`, `{notifications:[...]}`); `apiList` unwraps a named key.

async function parse(res: Response): Promise<any> {
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export async function apiGet<T = unknown>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`GET ${url} failed (${res.status})`);
  return (await parse(res)) as T;
}

// GET an endpoint that wraps its array under `key` (e.g. { events: [...] }).
export async function apiList<T = unknown>(url: string, key: string): Promise<T[]> {
  const data = await apiGet<Record<string, T[]>>(url);
  const arr = data?.[key];
  return Array.isArray(arr) ? arr : [];
}

export async function apiSend<T = unknown>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await parse(res);
  if (!res.ok || (data && data.success === false)) {
    throw new Error((data && data.error) || `${method} ${url} failed (${res.status})`);
  }
  return data as T;
}
